import type { ProtoShotPassDetail, ProtoEventsByMatch } from "@catbento/shared";
import type { CatalystSnapshot } from "./snapshot.js";
import { buildPlayersById, type EventContext } from "../engines/event-map-kit.js";

// Pure lookups for shot/pass events (Phase 02e). Grounded in
// renderers-shotmap.js + renderers-matches.js: shotPassDetail is keyed by player
// id and sparse (only one player captured), so a missing player honestly yields
// null / empty — the "quietly correct, not fabricated" degradation used
// throughout the prototype.

/**
 * One player's shot/pass detail (competitions + byCompetition→seasons→
 * shots/passes), or null if this player has no captured events. Keyed by the
 * player's numeric id (not name).
 */
export function getShotPassDetail(
  data: CatalystSnapshot,
  playerId: number | string
): ProtoShotPassDetail | null {
  return data.shotPassDetail?.[String(playerId)] ?? null;
}

/**
 * Flatten a player's events across every competition/season and group them by
 * match id — the join the Matches overlay needs (events attach to an appearance
 * purely by match_id, since shotPassDetail's own comp/season labels don't
 * string-match Appearances'). Reproduces renderers-matches.js's eventsByMatch.
 * A null detail yields empty maps.
 */
export function flattenEventsByMatch(detail: ProtoShotPassDetail | null): ProtoEventsByMatch {
  const shotsByMatch: ProtoEventsByMatch["shotsByMatch"] = {};
  const passesByMatch: ProtoEventsByMatch["passesByMatch"] = {};
  if (detail?.byCompetition) {
    for (const comp of Object.values(detail.byCompetition)) {
      for (const season of Object.values(comp.seasons ?? {})) {
        for (const s of season.shots ?? []) {
          (shotsByMatch[String(s.match_id)] ??= []).push(s);
        }
        for (const pa of season.passes ?? []) {
          (passesByMatch[String(pa.match_id)] ??= []).push(pa);
        }
      }
    }
  }
  return { shotsByMatch, passesByMatch };
}

/**
 * The EventMapKit's data context — the snapshot pieces its Opposition / Game State /
 * Recipient helpers read (the legacy reached for window.CatalystData inside them).
 * Missing blocks are empty: an uncaptured match honestly resolves to "no opponent".
 */
export function getEventContext(data: CatalystSnapshot): EventContext {
  return {
    matchResults: data.matchResults ?? {},
    matchGoals: data.matchGoals ?? {},
    playersById: buildPlayersById(data.players),
  };
}
